import { Link } from 'react-router-dom';
import { Heart, Shield, Clock, BarChart3, Lock, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlockyCharacter3D from '../components/BlockyCharacter3D';

const ForParentsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream via-warm-white to-lavender/10">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white border-2 border-coral-orange/30 rounded-full px-4 py-2 mb-8 shadow-md">
              <Heart className="w-4 h-4 text-coral-orange fill-coral-orange" />
              <span className="font-display text-sm text-coral-orange font-bold">FOR PARENTS</span>
            </div>
            
            <h1 className="font-display text-5xl md:text-6xl text-text-dark mb-6" data-testid="parents-headline">
              TYPING PRACTICE<br />
              <span className="text-mint-green">THEY'LL ACTUALLY LOVE</span>
            </h1>
            
            <p className="font-body text-lg text-text-medium max-w-2xl mx-auto mb-8">
              TypeTown Solo Mode turns typing practice into an adventure. Your child builds a town, 
              grows a blocky character, and learns to type, all while having fun.
            </p>
            
            <div className="flex justify-center gap-4 items-end mb-10">
              <BlockyCharacter3D size={100} skinTone="#5C3A21" hairColor="#B87333" shirtColor="#14B8A6" pantsColor="#1F2937" hairStyle="braids" accessory="headband" direction="right" />
              <BlockyCharacter3D size={140} skinTone="#3D2314" hairColor="#FF69B4" shirtColor="#EC4899" pantsColor="#7C3AED" hairStyle="afro" accessory="glasses" eyeStyle="happy" />
              <BlockyCharacter3D size={100} skinTone="#F5D0B0" hairColor="#00CED1" shirtColor="#F59E0B" pantsColor="#3B82F6" hairStyle="ponytail" accessory="bow" direction="left" />
            </div>
            
            <Link
              to="/login"
              data-testid="parents-signup-btn"
              className="font-display uppercase tracking-wider bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg inline-block"
            >
              Start Free &rarr;
            </Link>
            <p className="font-body text-sm text-text-medium mt-4">100% free &middot; No card required &middot; Full access for every child</p>
          </div>
        </div>
      </section>
      
      {/* Safety & Trust */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-display text-sm text-mint-green font-bold">SAFETY FIRST</span>
            <h2 className="font-display text-4xl text-text-dark mt-2">Designed with Parents in Mind</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: 'GDPR Compliant', desc: "Your child's data is protected by strict privacy standards." },
              { icon: Lock, title: 'No Ads or In-App Purchases', desc: 'No surprise bills. No distracting advertisements.' },
              { icon: Clock, title: 'Screen Time Controls', desc: 'Set daily limits and session reminders.' },
              { icon: BarChart3, title: 'Parent Dashboard', desc: "See your child's progress, accuracy, and time spent." },
            ].map((item, i) => (
              <div key={i} className="bg-white border-2 border-soft-gray/50 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all" data-testid={`safety-${i}`}>
                <item.icon className="w-8 h-8 text-mint-green mb-4" />
                <h3 className="font-display text-lg text-text-dark mb-2">{item.title}</h3>
                <p className="font-body text-sm text-text-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Why Typing Matters */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-display text-sm text-mint-green font-bold">WHY TYPING?</span>
              <h2 className="font-display text-4xl text-text-dark mt-2 mb-6">A Skill for Life</h2>
              <p className="font-body text-lg text-text-medium mb-6">
                For children who struggle with handwriting, typing can be transformative. 
                It opens up expression, reduces frustration, and builds confidence.
              </p>
              
              <div className="space-y-4">
                {[
                  'Children with dyspraxia often type faster than they write',
                  'Typing reduces the physical strain of handwriting',
                  'Digital skills are essential for school and beyond',
                  'Accuracy-first approach builds lasting habits',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-mint-green flex-shrink-0 mt-0.5" />
                    <span className="font-body text-text-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white border-2 border-soft-gray/50 rounded-2xl p-8 shadow-md">
              <h3 className="font-display text-xl text-text-dark mb-6">Solo Mode Includes:</h3>
              <div className="space-y-4">
                {[
                  'Personal town that grows with progress',
                  'Customisable Blocky companion',
                  '5 difficulty levels (Flicker to Breach)',
                  'Global weekly leaderboard',
                  'Full curriculum from basics to mastery',
                  'No time pressure - accuracy matters most',
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-mint-green rounded-full" />
                    <span className="font-body text-text-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Free for everyone */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-3xl mx-auto text-center">
          <span className="font-display text-sm text-mint-green font-bold">PRICING</span>
          <h2 className="font-display text-4xl text-text-dark mt-2 mb-2">Completely Free</h2>
          <p className="font-body text-text-medium mb-10">Every feature, every zone, every stage - free for every child. No subscriptions, no card required.</p>

          <div className="bg-white border-2 border-mint-green rounded-2xl p-8 shadow-md max-w-md mx-auto" data-testid="price-free">
            <div className="inline-flex items-center gap-2 bg-mint-green/10 rounded-full px-4 py-1 mb-4">
              <span className="font-display text-xs text-mint-green font-bold">FREE FOREVER</span>
            </div>
            <div className="font-display text-5xl text-mint-green mb-6">&pound;0</div>
            <ul className="space-y-3 mb-8 text-left">
              {[
                'Full town + all zones',
                'All Blocky stages',
                'Full character customisation',
                'All difficulty levels',
                'Global leaderboard',
                'Parent dashboard'
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-text-medium">
                  <CheckCircle className="w-4 h-4 text-mint-green flex-shrink-0" />
                  <span className="font-body text-sm">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/login"
              className="font-display uppercase tracking-wider bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all shadow-lg block text-center"
            >
              Start Free
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl text-text-dark mb-4">Give Your Child the Gift of Typing</h2>
          <p className="font-body text-text-medium mb-8">Start free today. No card required.</p>
          <Link
            to="/login"
            data-testid="parents-final-cta"
            className="font-display uppercase tracking-wider bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg inline-block"
          >
            Create Account &rarr;
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ForParentsPage;
